import { useDispatch, useSelector } from 'react-redux';
import { Button, Drawer, Spin, theme } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { TStore } from '@/common/basis/types/tstore';
import {
  setBookingMeetingType,
  setBookingRoomId,
  setCancelRoomId,
  setDetailRoomId,
  setIsOpenBookingDetail,
  setIsOpenBookingMeeting,
  setIsOpenCannelMeeting,
} from '@/common/store/booking/slice';
import { TBookingMeeting } from '@/common/basis/types/database/tbooking-meeting';
import { getBookingDetail } from '@/common/http/meet';
import { isSuccessCode } from '@/common/utils/response';
import { TPerson } from '@/common/basis/types/database/tperson';
import { decrypt } from '@/common/utils/crypto-js';
import { TCryptoJS } from '@/common/basis/types/tcrypto-js';
import { getUserAvatar } from '@/common/utils/image';
import { TField } from '@/common/basis/types/tfield';
import { getAutoMuteOptions, getRepeatOptions } from '@/common/utils/select-options';
import { setMeetingShareData } from '@/common/store/popup/slice';
import { useMeetingType } from '@/common/hooks/meeting-type';
import { useMeetingBrightPassword } from '@/common/hooks/meeting-bright-password';
import { joinParameters } from '@/common/utils/uri';
import CheckBeforeJoin from '@/components/HOC/CheckBeforeJoin';

function App() {
  const dispatch = useDispatch();
  const isOpenBookingDetail = useSelector((state: TStore.State) => state.booking.isOpenBookingDetail);
  const detailRoomId = useSelector((state: TStore.State) => state.booking.detailRoomId) ?? '';
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [detail, setDetail] = useState<TBookingMeeting.Data>();
  const [hostInfo, setHostInfo] = useState<TPerson.User>();
  const meetingType = detail?.meetingType ?? TField.MEETING_TYPE_BOOKING;
  const { isMeetingLive } = useMeetingType(meetingType);
  const stopTime = useMemo(() => detail?.stopTime, [detail?.stopTime]);
  const repeatType = useMemo(() => detail?.repeatType, [detail?.repeatType]);
  const password = useMemo(() => detail?.password ?? '', [detail?.password]);
  const nonce = useMemo(() => detail?.nonce ?? '', [detail?.nonce]);
  const autoMute = useMemo(() => detail?.autoMute, [detail?.autoMute]);
  const roomName = useMemo(() => detail?.roomName ?? '', [detail?.roomName]);
  const webUrl = useMemo(() => detail?.webUrl ?? '', [detail?.webUrl]);
  const isAllowAnonymous = useMemo(() => !!detail?.allowAnonymous, [detail?.allowAnonymous]);
  const liveUrl = useMemo(() => (isMeetingLive ? (detail?.liveUrl ?? '') : ''), [detail?.liveUrl, isMeetingLive]);
  const readyMode = useMemo(() => detail?.readyMode ?? '', [detail?.readyMode]);
  const recentStartTime = useMemo(() => detail?.recentStartTime, [detail?.recentStartTime]);
  const recentStopTime = useMemo(() => detail?.recentStopTime, [detail?.recentStopTime]);
  const { brightPassword } = useMeetingBrightPassword(password, nonce);
  const showReadyMode = useMemo(() => readyMode === TField.READY_MODE_ON, [readyMode]);
  const themeToken = theme.useToken();
  const { t } = useTranslation();
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]);
  const repeatTypeStr = useMemo(
    () =>
      repeatType === TField.MEET_REPEAT_TYPE_WORK // 兼容之前的预约会议 重复频率
        ? t('everyRepeat')
        : (getRepeatOptions().find((item) => item.value === repeatType)?.label ?? ''),
    [repeatType],
  );
  const autoMuteStr = useMemo(
    () => getAutoMuteOptions().find((item) => item.value === autoMute)?.label ?? '',
    [autoMute],
  );

  // 获取详情
  const requestBookingDetail = async () => {
    if (!detailRoomId) {
      return;
    }
    if (isBookingLoading) {
      return;
    }

    setIsBookingLoading(true);
    const resp = await getBookingDetail({ roomId: detailRoomId });

    if (isSuccessCode(resp?.code)) {
      const defaultHost = resp.data?.defaultHost;
      const hostName = resp.data?.hostName;

      setHostInfo({ userId: defaultHost, name: hostName });
      setDetail(resp.data);
    }
    setIsBookingLoading(false);
  };

  useEffect(() => {
    if (detailRoomId) {
      requestBookingDetail();
    }
  }, [detailRoomId]);

  useEffect(() => {
    if (!isOpenBookingDetail) {
      dispatch(setDetailRoomId(''));
    }
  }, [isOpenBookingDetail]);

  return (
    <>
      <Drawer
        onClose={() => dispatch(setIsOpenBookingDetail(false))}
        open={isOpenBookingDetail}
        styles={{
          header: { paddingTop: 28 },
          mask: { opacity: 0 },
          content: { backgroundColor: '#f2f3f5' },
          body: { padding: 0, margin: 0 },
        }}
        title={
          <div className='flex items-center justify-between'>
            <div className='w-14' />
            <span className='text-[18px]'>{t('meetingDetails')}</span>
            <div className='inline-flex items-center justify-center'>
              <Button
                color='default'
                icon={<EditOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
                onClick={() => {
                  dispatch(setIsOpenBookingDetail(false));
                  dispatch(setDetailRoomId(''));
                  dispatch(setIsOpenBookingMeeting(true));
                  dispatch(setBookingRoomId(detailRoomId));
                  dispatch(setBookingMeetingType(meetingType));
                }}
                variant='text'
              />
              <Button
                color='default'
                icon={<DeleteOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
                onClick={() => {
                  dispatch(setIsOpenCannelMeeting(true));
                  dispatch(setCancelRoomId(detailRoomId));
                }}
                variant='text'
              />
            </div>
          </div>
        }
        width={390}
      >
        {isBookingLoading ? (
          <div className='flex items-center justify-center pt-10'>
            <Spin />
          </div>
        ) : (
          <div className='relative flex h-full flex-col'>
            <div className='flex-1 overflow-y-auto'>
              <div className='overflow-hidden p-4'>
                <div className='rounded-xl bg-[#fff] p-3 py-4'>
                  <span className='break-words text-[16px] font-medium'>{roomName}</span>
                </div>

                <div className='my-4 rounded-xl bg-[#fff] py-4 text-[14px] text-[rgb(10,10,23)]'>
                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('meetingId')}</span>
                    </div>
                    <div className='font-medium'>
                      <span>{detailRoomId}</span>
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('meetingTime')}</span>
                    </div>
                    <div className='font-medium'>
                      {recentStartTime ? (
                        <div className='flex items-center justify-end'>
                          <span>{dayjs(recentStartTime).format(`${t('MMDDFormat')} HH:mm`)}</span>
                          <span className='mx-1'>-</span>
                          <span>{dayjs(recentStopTime).format(`${t('MMDDFormat')} HH:mm`)}</span>
                        </div>
                      ) : (
                        <div>
                          <span>-</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='flex min-h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('promoter')}</span>
                    </div>
                    <div className='py-2 font-medium'>
                      <div className='h-10 w-10 overflow-hidden rounded-sm'>
                        <img className='h-full w-full object-cover' src={getUserAvatar(hostInfo?.avatar)} />
                      </div>
                      <div className='mt-1 text-center'>
                        <span>{hostInfo?.name}</span>
                      </div>
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  {isRepeatMeeting ? (
                    <>
                      <div className='flex h-12 items-center justify-between px-3'>
                        <div>
                          <span>{t('repeatFrequency')}</span>
                        </div>
                        <div className='font-medium'>
                          <span>{repeatTypeStr}</span>
                        </div>
                      </div>
                      <div className='h-[1px] bg-[rgb(229,229,230)]' />
                      <div className='flex h-12 items-center justify-between px-3'>
                        <div>
                          <span>{t('repeatEnd')}</span>
                        </div>
                        <div className='font-medium'>
                          <span>{stopTime ? dayjs(stopTime).format(`${t('YYYYMMDDFormat')}`) : t('neverEnd')}</span>
                        </div>
                      </div>
                      <div className='h-[1px] bg-[rgb(229,229,230)]' />
                    </>
                  ) : (
                    <></>
                  )}

                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('anonymous')}</span>
                    </div>
                    <div className='font-medium'>
                      <span>{isAllowAnonymous ? t('anonymousAllow') : t('anonymousDisallow')}</span>
                    </div>
                  </div>
                  <div className='h-[1px] bg-[rgb(229,229,230)]' />

                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('joinPassword')}</span>
                    </div>
                    <div className='font-medium'>
                      <span>{brightPassword}</span>
                    </div>
                  </div>

                  <div className='h-[1px] bg-[rgb(229,229,230)]' />
                  <div className='flex h-12 items-center justify-between px-3'>
                    <div>
                      <span>{t('muteJoinMeeting')}</span>
                    </div>
                    <div className='font-medium'>
                      <span>{autoMuteStr}</span>
                    </div>
                  </div>
                  {showReadyMode && (
                    <>
                      <div className='h-[1px] bg-[rgb(229,229,230)]' />
                      <div className='flex h-12 items-center justify-between px-3'>
                        <div>
                          <span>{t('meetingManage')}</span>
                        </div>
                        <div className='font-medium'>
                          <span>{t('meetingManageOpen')}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className='flex h-14 items-center justify-center border-0 border-t-[1px] border-solid border-[rgba(19,20,21,.12)] bg-[#f7f7f7]'>
              <div
                className='flex h-full flex-1 cursor-pointer items-center justify-center text-[14px] font-medium'
                onClick={() => {
                  dispatch(
                    setMeetingShareData({
                      roomId: detailRoomId,
                      roomName,
                      brightPassword: decrypt({ str: password, key: nonce, iv: TCryptoJS.MEET_CLIENT_IV }),
                      webUrl,
                      liveUrl,
                    }),
                  );
                }}
              >
                <span style={{ color: themeToken.token.colorPrimary }}>{t('invite')}</span>
              </div>
              <div className='mx-1 h-6 w-[1px] rounded bg-[rgba(19,20,21,.12)]' />
              <div className='relative flex h-full flex-1 cursor-pointer items-center justify-center text-[14px] font-medium'>
                <CheckBeforeJoin
                  callback={(success) => success && window.open(joinParameters(webUrl, { showSchema: 0 }))}
                  roomId={detailRoomId}
                >
                  <span style={{ color: themeToken.token.colorPrimary }}>{t('enterMeeting')}</span>
                </CheckBeforeJoin>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}

export default App;
